import React, { useState, useEffect, useCallback } from 'react';
import {
    CalendarDays, Plus, Search, Clock, Video, Phone, MapPin, Users,
    Loader2, AlertTriangle, X, ChevronLeft, ChevronRight, MoreHorizontal,
    RefreshCw, Settings, Calendar, List, LayoutGrid, Link2, Check, XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    fetchEventTypes, fetchBookings, fetchSchedules, fetchCalendarStats,
    cancelBooking, confirmBooking
} from '../api/calendar';
import EventTypeCard from './EventTypeCard';
import EventTypeEditor from './EventTypeEditor';
import BookingModal from './BookingModal';
import AvailabilitySchedule from './AvailabilitySchedule';
import CalendarView from './CalendarView';
import ConfirmModal from './ConfirmModal';

const TABS = [
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'event-types', label: 'Event Types', icon: Clock },
    { id: 'availability', label: 'Availability', icon: Settings }
];

const BOOKING_FILTERS = [
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'past', label: 'Past' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'all', label: 'All' }
];

const CalendarPage = ({ currentUserEmail, cases = [] }) => {
    // Data State
    const [eventTypes, setEventTypes] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [stats, setStats] = useState({
        total_bookings: 0,
        upcoming_bookings: 0,
        completed_bookings: 0,
        cancelled_bookings: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI State
    const [activeTab, setActiveTab] = useState('bookings');
    const [bookingFilter, setBookingFilter] = useState('upcoming');
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'

    // Modal State
    const [isEventTypeEditorOpen, setIsEventTypeEditorOpen] = useState(false);
    const [editingEventType, setEditingEventType] = useState(null);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [isScheduleEditorOpen, setIsScheduleEditorOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, bookingId: null });
    const [isCancelling, setIsCancelling] = useState(false);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [eventTypesRes, bookingsRes, schedulesRes, statsRes] = await Promise.all([
                fetchEventTypes().catch(() => ({ data: [] })),
                fetchBookings({ status: bookingFilter }).catch(() => ({ data: [] })),
                fetchSchedules().catch(() => ({ data: [] })),
                fetchCalendarStats().catch(() => ({ data: {} }))
            ]);

            setEventTypes(eventTypesRes.data || []);
            setBookings(bookingsRes.data || []);
            setSchedules(schedulesRes.data || []);
            setStats(statsRes.data || {});
        } catch (err) {
            console.error('Failed to load calendar data:', err);
            setError(err.message || 'Failed to load calendar data');
        } finally {
            setLoading(false);
        }
    }, [bookingFilter]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter bookings by search
    const filteredBookings = bookings.filter(booking => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            booking.title?.toLowerCase().includes(term) ||
            booking.attendees?.some(a =>
                a.name?.toLowerCase().includes(term) ||
                a.email?.toLowerCase().includes(term)
            )
        );
    });

    // Handlers
    const handleCreateEventType = () => {
        setEditingEventType(null);
        setIsEventTypeEditorOpen(true);
    };

    const handleEditEventType = (eventType) => {
        setEditingEventType(eventType);
        setIsEventTypeEditorOpen(true);
    };

    const handleEventTypeSaved = (savedEventType) => {
        if (editingEventType) {
            setEventTypes(prev => prev.map(et => et.id === savedEventType.id ? savedEventType : et));
        } else {
            setEventTypes(prev => [savedEventType, ...prev]);
        }
        setIsEventTypeEditorOpen(false);
        setEditingEventType(null);
    };

    const handleEventTypeDeleted = (eventTypeId) => {
        setEventTypes(prev => prev.filter(et => et.id !== eventTypeId));
    };

    const handleViewBooking = (booking) => {
        setSelectedBooking(booking);
        setIsBookingModalOpen(true);
    };

    const handleCreateBooking = (eventType = null) => {
        setSelectedBooking(eventType ? { eventType } : null);
        setIsBookingModalOpen(true);
    };

    const handleBookingSaved = (savedBooking) => {
        if (selectedBooking?.id) {
            setBookings(prev => prev.map(b => b.id === savedBooking.id ? savedBooking : b));
        } else {
            setBookings(prev => [savedBooking, ...prev]);
        }
        setIsBookingModalOpen(false);
        setSelectedBooking(null);
        // Refresh stats
        fetchCalendarStats().then(res => setStats(res.data || {})).catch(() => {});
    };

    const handleCancelBookingClick = (bookingId) => {
        setConfirmModal({ isOpen: true, bookingId });
    };

    const handleConfirmCancel = async () => {
        const bookingId = confirmModal.bookingId;
        if (!bookingId) return;

        setIsCancelling(true);
        try {
            await cancelBooking(bookingId, { reason: 'Cancelled by host' });
            setBookings(prev => prev.map(b =>
                b.id === bookingId ? { ...b, status: 'cancelled', cancellation_reason: 'Cancelled by host' } : b
            ));
            // Refresh stats
            fetchCalendarStats().then(res => setStats(res.data || {})).catch(() => {});
            setConfirmModal({ isOpen: false, bookingId: null });
        } catch (err) {
            console.error('Failed to cancel booking:', err);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleCancelBooking = async (bookingId, reason) => {
        try {
            await cancelBooking(bookingId, { reason });
            setBookings(prev => prev.map(b =>
                b.id === bookingId ? { ...b, status: 'cancelled', cancellation_reason: reason } : b
            ));
            // Refresh stats
            fetchCalendarStats().then(res => setStats(res.data || {})).catch(() => {});
        } catch (err) {
            console.error('Failed to cancel booking:', err);
        }
    };

    const handleConfirmBooking = async (bookingId) => {
        try {
            await confirmBooking(bookingId);
            setBookings(prev => prev.map(b =>
                b.id === bookingId ? { ...b, status: 'confirmed' } : b
            ));
        } catch (err) {
            console.error('Failed to confirm booking:', err);
            alert('Failed to confirm booking: ' + err.message);
        }
    };

    const handleCreateSchedule = () => {
        setEditingSchedule(null);
        setIsScheduleEditorOpen(true);
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule(schedule);
        setIsScheduleEditorOpen(true);
    };

    const handleScheduleSaved = (savedSchedule) => {
        if (editingSchedule) {
            setSchedules(prev => prev.map(s => s.id === savedSchedule.id ? savedSchedule : s));
        } else {
            setSchedules(prev => [savedSchedule, ...prev]);
        }
        setIsScheduleEditorOpen(false);
        setEditingSchedule(null);
    };

    const handleScheduleDeleted = (scheduleId) => {
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getLocationIcon = (locationType) => {
        switch (locationType) {
            case 'video': return Video;
            case 'phone': return Phone;
            case 'in_person': return MapPin;
            default: return Link2;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'text-emerald-500 bg-emerald-500/10';
            case 'pending': return 'text-yellow-500 bg-yellow-500/10';
            case 'cancelled': return 'text-red-500 bg-red-500/10';
            default: return 'text-slate-500 bg-slate-500/10';
        }
    };

    return (
        <div className="h-full dark:bg-slate-950 bg-slate-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold dark:text-white text-slate-900 flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg dark:shadow-yellow-900/20 shadow-yellow-500/10">
                                <CalendarDays className="text-white" size={24} />
                            </div>
                            Calendar
                        </h1>
                        <p className="dark:text-slate-400 text-slate-500 text-sm mt-1 ml-14">
                            Manage your appointments, event types, and availability.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Refresh */}
                        <button
                            onClick={loadData}
                            disabled={loading}
                            className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100 dark:text-slate-400 text-slate-600 hover:text-slate-900 dark:hover:text-white dark:hover:bg-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
                            title="Refresh"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>

                        {/* View Toggle (only for bookings) */}
                        {activeTab === 'bookings' && (
                            <div className="flex dark:bg-slate-800 bg-slate-100 p-1 rounded-lg border dark:border-slate-700 border-slate-200">
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                                        ? 'dark:bg-slate-600 bg-white dark:text-white text-yellow-600 shadow-md'
                                        : 'text-slate-400 hover:text-yellow-600 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700/50'
                                    }`}
                                    title="List View"
                                >
                                    <List size={18} />
                                </button>
                                <button
                                    onClick={() => setViewMode('calendar')}
                                    className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'calendar'
                                        ? 'dark:bg-slate-600 bg-white dark:text-white text-yellow-600 shadow-md'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700/50'
                                    }`}
                                    title="Calendar View"
                                >
                                    <Calendar size={18} />
                                </button>
                            </div>
                        )}

                        {/* Action Button */}
                        {activeTab === 'bookings' && (
                            <button
                                onClick={() => handleCreateBooking()}
                                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-yellow-900/20"
                            >
                                <Plus size={18} />
                                New Booking
                            </button>
                        )}
                        {activeTab === 'event-types' && (
                            <button
                                onClick={handleCreateEventType}
                                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-yellow-900/20"
                            >
                                <Plus size={18} />
                                New Event Type
                            </button>
                        )}
                        {activeTab === 'availability' && (
                            <button
                                onClick={handleCreateSchedule}
                                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-yellow-900/20"
                            >
                                <Plus size={18} />
                                New Schedule
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { label: 'Upcoming', count: stats.upcoming_bookings || 0, color: 'sky', onClick: () => { setActiveTab('bookings'); setBookingFilter('upcoming'); } },
                        { label: 'Completed', count: stats.completed_bookings || 0, color: 'emerald', onClick: () => { setActiveTab('bookings'); setBookingFilter('past'); } },
                        { label: 'Cancelled', count: stats.cancelled_bookings || 0, color: 'red', onClick: () => { setActiveTab('bookings'); setBookingFilter('cancelled'); } },
                        { label: 'Event Types', count: eventTypes.length, color: 'purple', onClick: () => setActiveTab('event-types') }
                    ].map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            onClick={stat.onClick}
                            className={`p-4 rounded-xl border cursor-pointer transition-all group dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 hover:border-${stat.color}-500/50 dark:hover:bg-slate-800/50 hover:bg-slate-50`}
                        >
                            <p className="text-xs font-bold uppercase tracking-wider dark:text-slate-500 text-slate-400">{stat.label}</p>
                            <h3 className={`text-2xl font-bold mt-1 text-${stat.color}-500 group-hover:scale-105 transition-transform origin-left`}>
                                {loading ? '-' : stat.count}
                            </h3>
                        </motion.div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b dark:border-slate-800 border-slate-200 -mx-8 px-8">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${activeTab === tab.id
                                ? 'dark:text-yellow-400 text-yellow-600 border-yellow-500'
                                : 'dark:text-slate-400 text-slate-500 border-transparent hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Search & Filters Bar (for bookings tab) */}
            {activeTab === 'bookings' && (
                <div className="px-8 py-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/30 bg-white/50 flex items-center gap-4">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search bookings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {/* Booking Filters */}
                    <div className="flex items-center gap-2">
                        {BOOKING_FILTERS.map(filter => (
                            <button
                                key={filter.id}
                                onClick={() => setBookingFilter(filter.id)}
                                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${bookingFilter === filter.id
                                    ? 'bg-yellow-500 text-white'
                                    : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-600 hover:bg-yellow-500/20 hover:text-yellow-600 dark:hover:text-yellow-400'
                                }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 size={32} className="text-yellow-500 animate-spin mb-3" />
                        <p className="text-sm dark:text-slate-400 text-slate-500">Loading calendar data...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-4 rounded-full bg-red-500/10 mb-4">
                            <AlertTriangle size={32} className="text-red-500" />
                        </div>
                        <p className="text-sm text-red-500 mb-4">{error}</p>
                        <button
                            onClick={loadData}
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm transition"
                        >
                            Try Again
                        </button>
                    </div>
                ) : (
                    <AnimatePresence mode="wait">
                        {/* Bookings Tab */}
                        {activeTab === 'bookings' && (
                            <motion.div
                                key="bookings"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                {viewMode === 'calendar' ? (
                                    <CalendarView
                                        bookings={filteredBookings}
                                        onSelectBooking={handleViewBooking}
                                        onCreateBooking={handleCreateBooking}
                                    />
                                ) : filteredBookings.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-64">
                                        <div className="p-6 rounded-2xl dark:bg-slate-800/50 bg-slate-100 mb-4">
                                            <Calendar size={48} className="dark:text-slate-600 text-slate-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
                                            {searchTerm ? 'No matching bookings' : `No ${bookingFilter} bookings`}
                                        </h3>
                                        <p className="text-sm dark:text-slate-400 text-slate-500 mb-6 text-center max-w-md">
                                            {searchTerm ? 'Try a different search term.' : 'Your scheduled appointments will appear here.'}
                                        </p>
                                        {!searchTerm && bookingFilter === 'upcoming' && (
                                            <button
                                                onClick={() => handleCreateBooking()}
                                                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105"
                                            >
                                                <Plus size={18} />
                                                Create Booking
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {filteredBookings.map((booking, index) => {
                                            const LocationIcon = getLocationIcon(booking.location_type);
                                            return (
                                                <motion.div
                                                    key={booking.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.03 }}
                                                    onClick={() => handleViewBooking(booking)}
                                                    className="p-4 rounded-xl border dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 hover:border-yellow-500/50 dark:hover:bg-slate-800/50 hover:bg-slate-50 cursor-pointer transition-all group"
                                                >
                                                    <div className="flex items-start gap-4">
                                                        {/* Color Bar */}
                                                        <div
                                                            className="w-1 h-16 rounded-full"
                                                            style={{ backgroundColor: booking.event_type?.color || '#3B82F6' }}
                                                        />

                                                        {/* Main Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div>
                                                                    <h3 className="font-semibold dark:text-white text-slate-900 truncate">
                                                                        {booking.title}
                                                                    </h3>
                                                                    <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">
                                                                        {formatDateTime(booking.start_time)}
                                                                    </p>
                                                                </div>
                                                                <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(booking.status)}`}>
                                                                    {booking.status}
                                                                </span>
                                                            </div>

                                                            <div className="flex items-center gap-4 mt-3">
                                                                {/* Event Type */}
                                                                <div className="flex items-center gap-1.5 text-sm dark:text-slate-400 text-slate-500">
                                                                    <Clock size={14} />
                                                                    <span>{booking.event_type?.duration || 30} min</span>
                                                                </div>

                                                                {/* Location */}
                                                                <div className="flex items-center gap-1.5 text-sm dark:text-slate-400 text-slate-500">
                                                                    <LocationIcon size={14} />
                                                                    <span className="capitalize">{booking.location_type?.replace('_', ' ') || 'Video'}</span>
                                                                </div>

                                                                {/* Attendees */}
                                                                {booking.attendees?.length > 0 && (
                                                                    <div className="flex items-center gap-1.5 text-sm dark:text-slate-400 text-slate-500">
                                                                        <Users size={14} />
                                                                        <span>{booking.attendees[0]?.name}</span>
                                                                        {booking.attendees.length > 1 && (
                                                                            <span className="text-xs dark:text-slate-500 text-slate-400">
                                                                                +{booking.attendees.length - 1}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Case Link */}
                                                                {booking.case_id && (
                                                                    <div className="flex items-center gap-1.5 text-sm text-yellow-500">
                                                                        <Link2 size={14} />
                                                                        <span>Linked to case</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            {booking.status === 'pending' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleConfirmBooking(booking.id);
                                                                    }}
                                                                    className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition"
                                                                    title="Confirm"
                                                                >
                                                                    <Check size={18} />
                                                                </button>
                                                            )}
                                                            {booking.status !== 'cancelled' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCancelBookingClick(booking.id);
                                                                    }}
                                                                    className="p-2 rounded-lg text-red-500 hover:bg-red-500/10 transition"
                                                                    title="Cancel"
                                                                >
                                                                    <XCircle size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Event Types Tab */}
                        {activeTab === 'event-types' && (
                            <motion.div
                                key="event-types"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                {eventTypes.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-64">
                                        <div className="p-6 rounded-2xl dark:bg-slate-800/50 bg-slate-100 mb-4">
                                            <Clock size={48} className="dark:text-slate-600 text-slate-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
                                            No event types yet
                                        </h3>
                                        <p className="text-sm dark:text-slate-400 text-slate-500 mb-6 text-center max-w-md">
                                            Create event types to define different meeting durations and settings.
                                        </p>
                                        <button
                                            onClick={handleCreateEventType}
                                            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105"
                                        >
                                            <Plus size={18} />
                                            Create Event Type
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {eventTypes.map((eventType, index) => (
                                            <motion.div
                                                key={eventType.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                            >
                                                <EventTypeCard
                                                    eventType={eventType}
                                                    onEdit={() => handleEditEventType(eventType)}
                                                    onDelete={() => handleEventTypeDeleted(eventType.id)}
                                                    onBook={() => handleCreateBooking(eventType)}
                                                />
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Availability Tab */}
                        {activeTab === 'availability' && (
                            <motion.div
                                key="availability"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                <AvailabilitySchedule
                                    schedules={schedules}
                                    onCreateSchedule={handleCreateSchedule}
                                    onEditSchedule={handleEditSchedule}
                                    onScheduleSaved={handleScheduleSaved}
                                    onScheduleDeleted={handleScheduleDeleted}
                                    isEditorOpen={isScheduleEditorOpen}
                                    setIsEditorOpen={setIsScheduleEditorOpen}
                                    editingSchedule={editingSchedule}
                                    setEditingSchedule={setEditingSchedule}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}
            </div>

            {/* Event Type Editor Modal */}
            <AnimatePresence>
                {isEventTypeEditorOpen && (
                    <EventTypeEditor
                        eventType={editingEventType}
                        schedules={schedules}
                        onSave={handleEventTypeSaved}
                        onClose={() => {
                            setIsEventTypeEditorOpen(false);
                            setEditingEventType(null);
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Booking Modal */}
            <AnimatePresence>
                {isBookingModalOpen && (
                    <BookingModal
                        booking={selectedBooking}
                        eventTypes={eventTypes}
                        cases={cases}
                        isLoadingEventTypes={loading}
                        onSave={handleBookingSaved}
                        onCancel={handleCancelBooking}
                        onClose={() => {
                            setIsBookingModalOpen(false);
                            setSelectedBooking(null);
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Cancel Booking Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, bookingId: null })}
                onConfirm={handleConfirmCancel}
                title="Cancel Booking"
                message="Are you sure you want to cancel this booking? This action cannot be undone and the attendee will be notified."
                confirmText="Cancel Booking"
                cancelText="Keep Booking"
                variant="danger"
                isLoading={isCancelling}
            />
        </div>
    );
};

export default CalendarPage;
